import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
} from "@mui/material";
import {
  GENDERS,
  UNIFORM_SIZES,
  UNIFORM_VARIANTS,
} from "../utils/uniformConstants";

const ItemDialog = ({
  open,
  onClose,
  onSave,
  form,
  setForm,
  masters,
  categories,
  isEditing,
  lockProductLine = false,
}) => {
  const isUniform = form.productLine === "uniform";
  const showProductLineToggle = !isEditing && !lockProductLine;

  const validate = () => {
    if (!form.name.trim() && !isUniform) return "Name is required";
    if (!form.price || form.price <= 0) return "Valid price is required";
    if (isUniform) {
      if (!form.uniformGroup?.trim()) return "Product group is required";
      if (!form.gender) return "Gender is required";
      if (!form.uniformVariant) return "Variant is required";
      if (!form.size?.trim()) return "Size is required";
    }
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) return alert(error);
    onSave();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? "Edit" : "Add"} {isUniform ? "Uniform SKU" : "Book Item"}
      </DialogTitle>
      <DialogContent>
        {showProductLineToggle && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
              Product line
            </Typography>
            <ToggleButtonGroup
              value={form.productLine || "book"}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  setForm((f) => ({
                    ...f,
                    productLine: value,
                    programs: value === "uniform" ? [] : f.programs,
                  }));
                }
              }}
              size="small"
              color="primary"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="book">Book</ToggleButton>
              <ToggleButton value="uniform">Uniform</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}

        {isUniform ? (
          <>
            <TextField
              label="Product group"
              value={form.uniformGroup || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, uniformGroup: e.target.value }))
              }
              fullWidth
              margin="normal"
              required
              helperText='e.g. "Boys Shirt", "Girls Skirt"'
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Gender</InputLabel>
              <Select
                value={form.gender || ""}
                label="Gender"
                onChange={(e) =>
                  setForm((f) => ({ ...f, gender: e.target.value }))
                }
              >
                {GENDERS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Variant</InputLabel>
              <Select
                value={form.uniformVariant || ""}
                label="Variant"
                onChange={(e) =>
                  setForm((f) => ({ ...f, uniformVariant: e.target.value }))
                }
              >
                {UNIFORM_VARIANTS.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Size</InputLabel>
              <Select
                value={form.size || ""}
                label="Size"
                onChange={(e) =>
                  setForm((f) => ({ ...f, size: e.target.value }))
                }
              >
                {UNIFORM_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Display name (optional)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              margin="normal"
              helperText="Leave blank to auto-generate from group, variant, and size"
            />
          </>
        ) : (
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            margin="normal"
            required
          />
        )}

        <TextField
          label="Description"
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
          fullWidth
          margin="normal"
        />
        <TextField
          label="Price"
          type="number"
          value={form.price}
          onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          fullWidth
          margin="normal"
          required
        />
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Cost Type
        </Typography>
        <ToggleButtonGroup
          value={form.costMode}
          exclusive
          onChange={(e, value) => {
            if (value) {
              setForm((f) => ({ ...f, costMode: value }));
            }
          }}
          size="small"
          color="primary"
        >
          <ToggleButton value="costPrice">Cost Amount</ToggleButton>
          <ToggleButton value="commissionPercent">Commission %</ToggleButton>
        </ToggleButtonGroup>

        {form.costMode === "costPrice" && (
          <TextField
            label="Cost Amount"
            type="number"
            value={form.costPrice}
            onChange={(e) =>
              setForm((f) => ({ ...f, costPrice: e.target.value }))
            }
            fullWidth
            sx={{ mt: 2 }}
          />
        )}

        {form.costMode === "commissionPercent" && (
          <TextField
            label="Commission %"
            type="number"
            value={form.commissionPercent}
            onChange={(e) =>
              setForm((f) => ({ ...f, commissionPercent: e.target.value }))
            }
            fullWidth
            sx={{ mt: 2 }}
          />
        )}

        {!isUniform && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              multiple
              value={form.category}
              onChange={(e) =>
                setForm((f) => ({ ...f, category: e.target.value }))
              }
              renderValue={(selected) => selected.join(", ")}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary">
          {isUniform
            ? "Eligible grades (optional — leave empty for all classes)"
            : "Class / program mapping"}
        </Typography>

        {isUniform ? (
          <FormControl fullWidth margin="normal">
            <InputLabel>Grades</InputLabel>
            <Select
              multiple
              value={form.grades}
              onChange={(e) =>
                setForm((f) => ({ ...f, grades: e.target.value }))
              }
              renderValue={(selected) => selected.join(", ")}
            >
              {masters.grades.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          ["grades", "programs", "subjects"].map((field) => (
            <FormControl fullWidth margin="normal" key={field}>
              <InputLabel>
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </InputLabel>
              <Select
                multiple
                value={form[field]}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [field]: e.target.value }))
                }
                renderValue={(selected) => selected.join(", ")}
              >
                {masters[field].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {isEditing ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemDialog;
